import {
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import type {
    CreateProductPayload,
    Product,
    UpdateProductPayload,
} from '@/lib/api/product-api'
import { productApi } from '@/lib/api/product-api'

export const productKeys = {
    all: ['products'] as const,
    list: (params?: Record<string, string | number | boolean | undefined>) =>
        [...productKeys.all, 'list', params] as const,
    detail: (id: string) => [...productKeys.all, 'detail', id] as const,
    stockBreakdown: (id: string) =>
        [...productKeys.all, 'stock-breakdown', id] as const,
}

export function useProducts(
    params?: Record<string, string | number | boolean | undefined>,
    enabled = true,
) {
    return useQuery({
        queryKey: productKeys.list(params),
        queryFn: () => productApi.list(params),
        enabled,
    })
}

/** Tổng số SP khớp bộ lọc (chỉ xin 1 dòng). Cho các gợi ý kiểu "danh mục này
 *  chưa có sản phẩm" — ô chọn giờ tải dần nên không đếm được từ options.
 *  `undefined` khi chưa tải xong hoặc bị tắt. */
export function useProductCount(
    params: Record<string, string | number | boolean | undefined>,
    enabled = true,
): number | undefined {
    const { data } = useProducts({ ...params, per_page: 1 }, enabled)
    return data?.meta?.total
}

// Danh sách SP tải dần theo trang (cuộn tới cuối thì gọi fetchNextPage) — cho
// ô chọn SP duyệt cả catalog mà không nạp một lần per_page lớn.
export function useInfiniteProducts(
    params: Record<string, string | number | boolean | undefined>,
    enabled = true,
) {
    return useInfiniteQuery({
        queryKey: [...productKeys.all, 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            productApi.list({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
        enabled,
    })
}

export function useProduct(id: string) {
    return useQuery({
        queryKey: productKeys.detail(id),
        queryFn: async () => {
            const res = await productApi.getById(id)
            return res.data!
        },
        enabled: !!id,
    })
}

/** Tải nhiều SP theo id (dùng chung cache chi tiết với useProduct), trả map
 *  id → SP. Cho chỗ cần thuộc tính của ĐÚNG các SP đã chọn (vd định lượng hộp
 *  ở sổ đóng hộp) mà không nạp cả danh mục. Lưu ý: GetByID không có tồn. */
export function useProductsByIds(ids: readonly string[]): Map<string, Product> {
    const unique = [...new Set(ids.filter(Boolean))]
    return useQueries({
        queries: unique.map((id) => ({
            queryKey: productKeys.detail(id),
            queryFn: async () => {
                const res = await productApi.getById(id)
                return res.data!
            },
        })),
        combine: (results) => {
            const map = new Map<string, Product>()
            for (const r of results) if (r.data) map.set(r.data.id, r.data)
            return map
        },
    })
}

/** Tồn khả dụng gộp theo kho / vị trí / mức CK — thẻ tồn kho màn chi tiết SP. */
export function useProductStockBreakdown(id: string) {
    return useQuery({
        queryKey: productKeys.stockBreakdown(id),
        queryFn: async () => {
            const res = await productApi.getStockBreakdown(id)
            return res.data!
        },
        enabled: !!id,
    })
}

export function useCreateProduct() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateProductPayload) => productApi.create(data),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
    })
}

export function useUpdateProduct() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: UpdateProductPayload
        }) => productApi.update(id, data),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
    })
}

export function useDeleteProduct() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productApi.delete(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
    })
}

export function useUploadProductImage() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ productId, file }: { productId: string; file: File }) =>
            productApi.uploadImage(productId, file),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
    })
}

export function useImportProducts() {
    return useMutation({
        mutationFn: (file: File) => productApi.importExcel(file),
    })
}
